import { FC, useMemo, useState } from 'react'
import { Button, Flex, Table } from 'antd'
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined'
import qs from 'qs'
import { TableRowSelection } from 'antd/es/table/interface'
import '../../../orders.css'
import useEndpointQueryParams, {
    DeserializeFilters,
} from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import { EndpointQueryParameters } from '../../../../../types/strapi/strapi'
import { useGetMyOrderRequestsQuery } from '../../../../../store/api/order-requests-api'
import useTablePagination from '../../../../../hooks/table-pages/use-table-pagination'
import OrderRequest from '../../../../../types/api/order-request'
import FormModal from '../../../../../components/form-modal/form-modal'
import GroupOperationsForm from './components/group-operations-form/group-operations-form'
import TableWrapper from '../../../../../components/table-wrapper/table-wrapper'
import FiltersForm from './components/filters-form/filters-form'
import { columns } from './constants/columns'
import FormValues from './components/filters-form/types/form-values'
import { buildFilters } from './helpers/build-filters'

const deserializeFilters: DeserializeFilters = filtersString => {
    const formValues = qs.parse(filtersString) as Partial<FormValues>
    return buildFilters(formValues)
}

interface OrdersProps {}

const Orders: FC<OrdersProps> = () => {
    const params = useEndpointQueryParams({ deserializeFilters })
    const orderRequestsParams: EndpointQueryParameters = {
        populate: {
            initiator: true,
            unit: true,
            material: {
                populate: {
                    material_type: true,
                },
            },
            shipment: true,
        },
        pagination: params.pagination,
        filters: params.filters,
        sort: ['requestStatus:asc', 'createdAt:desc'],
    }

    const { data, isLoading, refetch } = useGetMyOrderRequestsQuery(orderRequestsParams, {
        refetchOnMountOrArgChange: true,
    })
    const pagination = useTablePagination(params, data)
    const dataSource: OrderRequest[] = useMemo(() => data?.data || [], [data])
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
    }

    const rowSelection: TableRowSelection<OrderRequest> = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: OrderRequest) => ({
            disabled: record.requestStatus === 'Обработан',
        }),
    }
    const rowClassName = (record: OrderRequest): string => {
        switch (record.requestStatus) {
            case 'Обработан':
                return 'processed-row'
            case 'Отменен':
                return 'disabled-row'

            default:
                return ''
        }
    }

    const selectedOrders = useMemo(() => {
        return selectedRowKeys
            .map(docId => dataSource.find(item => item.documentId === docId))
            .filter(item => !!item) as OrderRequest[]
    }, [selectedRowKeys, dataSource])

    const hasSelected = selectedRowKeys.length > 0

    return (
        <>
            <Flex style={{ flex: '0 0 auto', marginBottom: '16px' }} justify={'end'}>
                <FormModal
                    title={'Заказы материалов'}
                    width={1400}
                    renderOpener={onOpen => (
                        <Button
                            type={'primary'}
                            size={'middle'}
                            icon={<PlusOutlined />}
                            disabled={!hasSelected}
                            onClick={onOpen}
                            title={'Заказать'}
                        >
                            Заказать
                        </Button>
                    )}
                    renderContent={onClose => (
                        <GroupOperationsForm
                            selectedOrders={selectedOrders}
                            onSuccess={() => {
                                refetch()
                                setSelectedRowKeys([])
                                onClose()
                            }}
                        />
                    )}
                />
            </Flex>
            {/* TODO Поправить проблему с высотой таблицы*/}
            <TableWrapper style={{ flex: 'auto' }} currentTableHeight={75}>
                <Table
                    bordered
                    columns={columns}
                    rowSelection={rowSelection}
                    rowClassName={rowClassName}
                    dataSource={dataSource}
                    pagination={pagination}
                    loading={isLoading}
                    rowKey={'documentId'}
                    summary={() => (
                        <Table.Summary fixed={'top'}>
                            <FiltersForm />
                        </Table.Summary>
                    )}
                />
            </TableWrapper>
        </>
    )
}

export default Orders
